/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import { EasOsdPosition } from "./EasOsdPosition";
import type { EasOsdProps } from "./EasOsdProps";

import "./EasOsd.scss";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "EasOsd";

// Fixed per the EAS OSD specification: Top/Bottom show the English title only;
// Full Screen shows both lines regardless of the active UI locale.
const EAS_TITLE_PRIMARY: string = "EMERGENCY ALERT";
const EAS_TITLE_SECONDARY: string = "ALERTE D'URGENCE";
const EAS_DISMISS_FOOTER: string = "Press BACK to dismiss";

/**
 * EasOsd — Emergency Alert System (EAS) On Screen Display template.
 *
 * Presentational only: renders the alert bar for the supplied country-driven
 * {@link EasOsdProps.configuration}. Placement and the
 * "Press BACK to dismiss" footer are derived from that config; this
 * component owns NO dismiss-key or lifecycle logic of its own.
 *
 * The "Press BACK to dismiss" footer is shown exactly when
 * {@link EasOsdProps.isDismissible} is set, and that same value is what
 * `EasInputGate` gates the dismiss key on. The two cannot drift apart,
 * because they are one decision taken once in `EasDismissPolicy` — which
 * requires both that the market's template carries a footer and that the
 * alert permits exit. A template with no footer therefore has no dismissal,
 * which is what Canada's full-screen variant relies on.
 *
 * Placement remains the market's decision and is carried by
 * {@link EasOsdProps.configuration}; it has no say in dismissal.
 *
 * The bilingual (English / French) title is fixed to the `FullScreen`
 * template (Canada) and is always shown there — it is not a configurable
 * option.
 *
 * When {@link EasOsdProps.hasVideo} is set the backdrop is cleared so the
 * alert's own picture shows through. The message panel keeps its dark
 * background regardless, because an emergency message has to stay readable
 * over whatever the video happens to be showing.
 */
const EasOsd: FunctionComponent<EasOsdProps> = (
  props: EasOsdProps
): JSX.Element => {
  const { message, configuration, id, hasVideo, isDismissible } = props;
  const { position } = configuration;

  // The one condition. Whether this market's template offers a footer at all
  // is already folded into `isDismissible` by `EasDismissPolicy`, so there is
  // no position test here — a second condition could only disagree with the
  // gate, which is the bug this shape exists to make impossible.
  const showDismissFooter: boolean = isDismissible;
  const showSecondaryTitle: boolean = position === EasOsdPosition.FullScreen;
  const hasId: boolean = id !== undefined && id !== "";
  // The backdrop is what would otherwise paint over the alert's own video.
  const videoModifier: string = hasVideo === true ? " eas-osd--with-video" : "";

  return (
    <div
      className={`eas-osd eas-osd--${position}${videoModifier}`}
      data-testid="eas-osd"
    >
      <div className="eas-osd__panel" role="alert" aria-live="assertive">
        {hasId ? (
          <div className="eas-osd__id" data-testid="eas-osd-id">
            {id}
          </div>
        ) : null}
        <div className="eas-osd__title" data-testid="eas-osd-title">
          {EAS_TITLE_PRIMARY}
        </div>
        <div className="eas-osd__body">
          <span className="eas-osd__body-text" data-testid="eas-osd-message">
            {message}
          </span>
        </div>
        {showSecondaryTitle ? (
          <div
            className="eas-osd__title eas-osd__title--secondary"
            data-testid="eas-osd-title-secondary"
          >
            {EAS_TITLE_SECONDARY}
          </div>
        ) : null}
        {showDismissFooter ? (
          <div className="eas-osd__footer" data-testid="eas-osd-footer">
            {EAS_DISMISS_FOOTER}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EasOsd;
